import React, { useEffect, useState, ReactElement, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useToasts } from 'react-toast-notifications';
import { ApolloProvider, ApolloClient, HttpLink, InMemoryCache } from '@apollo/client';

import { Section, Notification, DocumentUpload } from '@prenuvo/shared-library';
import { getApiUri } from 'packages/locations';
import {
  useCreateDocument,
  useGetActiveDocuments,
  useGetDocumentTypes,
  useDeactivateDocument,
  useGetLatestActiveDocument,
} from '../../hooks/documents';
import { DocumentRecord } from '../../hooks/documents/useGetActiveDocuments';
import { buildStudySelector } from '../../store/studies/selectors';
import { buildPatientSelector } from '../../store/patients/selectors';
import { selectSession } from '../../store/session/selectors';
import { DocumentStatus, DocumentTypeLabels } from '../../constants/documents';
import { getErrorMessage } from '../../utils/error';
import { useConfirmBodyCompQC } from '../../hooks/useConfirmBodyCompQC';
import { addLogItem } from '../../store/studies/actions';

const MESSAGES = {
  SUCCESS: 'Body Composition Initial Check Approved Successfully',
};

const StudyDocuments: React.FC = () => {
  const [tableData, setTableData] = useState<(DocumentRecord & { id: string })[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const { studyId: selectedStudyId } = useParams<{ studyId: string }>();

  const selectStudy = buildStudySelector(selectedStudyId);
  const study = useSelector(selectStudy);
  const patient = useSelector(buildPatientSelector(study.patient));
  const { data: session } = useSelector(selectSession);

  const { addToast } = useToasts();
  const { createDocument } = useCreateDocument();
  const { documents, loading: isFetchingDocument, failedFetchIds, refetch } = useGetActiveDocuments(
    selectedStudyId,
    study.poetryMetadata,
  );
  const { fetchDocument } = useGetLatestActiveDocument();
  const {
    documentTypes,
    loading: isFetchingDocumentTypes,
    error: errorFetchingDocumentTypes,
  } = useGetDocumentTypes();
  const { confirmBodyCompQC } = useConfirmBodyCompQC(selectedStudyId);
  const dispatch = useDispatch();

  const userRoles = session?.roles || [];
  const { deactivateDocument } = useDeactivateDocument();

  const fetchStudyDocument = async (typeId: string, _?: string, document?: DocumentRecord) => {
    try {
      const carePlanId = document?.carePlanId;
      const careActivityId = document?.careActivityId;

      const result = await fetchDocument(typeId, selectedStudyId, carePlanId, careActivityId);
      const activeDocument = result?.data?.getLatestActiveDocument;

      if (!activeDocument) {
        addToast(Notification.create('Document Fetch Failed', 'Document not found'), {
          appearance: 'error',
        });
        return null;
      }

      return {
        base64File: activeDocument.base64File,
        fileType: activeDocument.fileType,
      };
    } catch (err) {
      const errorMessage = getErrorMessage(err);
      addToast(Notification.create('Error', errorMessage), {
        appearance: 'error',
      });
      return null;
    }
  };

  const parseDataUrl = (dataUrl: string): { fileType: string; base64File: string } => {
    if (typeof dataUrl !== 'string') {
      throw new Error('Expected a Data-URL string');
    }
    const match = dataUrl.match(/^data:(.+);base64,(.+)$/);
    if (!match) {
      throw new Error('Invalid Data-URL format');
    }
    const [, fileType, base64File] = match;
    return { fileType, base64File };
  };

  const getDocumentStatusByType = (typeId: string): string => {
    const documentType = documentTypes?.find((type) => type.uuid === typeId);
    const typeName = documentType?.name;

    if (typeName && (typeName.includes('BLOOD') || typeName.includes('RADIOLOGY'))) {
      return DocumentStatus.DRAFT_READY;
    }

    if (typeName && typeName.includes('DOCTYPE.BODY.COMPOSITION')) {
      return DocumentStatus.INITIAL_CHECK;
    }

    return DocumentStatus.PENDING_QC;
  };

  const buildDocumentInput = (baseDocument: {
    patientId: string;
    typeId: string;
    status: string;
    name?: string;
    base64File?: string;
    fileType?: string;
    carePlanId?: string;
    careActivityId?: string;
    reason?: string;
  }) => {
    if (baseDocument.carePlanId && baseDocument.careActivityId) {
      return baseDocument;
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { carePlanId, careActivityId, ...studyDocument } = baseDocument;
    return {
      ...studyDocument,
      studyId: selectedStudyId,
    };
  };

  useEffect(() => {
    if (!isFetchingDocument && documents && documents.length > 0) {
      const updatedDocuments = documents.map((document) => ({
        ...document,
        id: document.uuid,
        shouldBypassDraftReady: study?.items?.primary?.skuID === '370',
      }));
      setTableData(updatedDocuments);
    }
  }, [isFetchingDocument, documents, study?.items?.primary?.skuID]);

  useEffect(() => {
    if (errorFetchingDocumentTypes) {
      addToast(
        Notification.create(
          'Fetching Document Types Failed',
          'There was an error fetching document types. Please try again.',
        ),
        { appearance: 'error' },
      );
    }
  }, [errorFetchingDocumentTypes]);

  const typeOptions = useMemo(
    () =>
      (documentTypes ?? []).map((documentType) => {
        const key = documentType.name;
        const label = DocumentTypeLabels[key] ?? documentType.name;
        return {
          value: documentType.uuid,
          label,
        };
      }),
    [documentTypes],
  );

  const failedFetchIdsKey = useMemo(() => failedFetchIds.join(','), [failedFetchIds]);

  useEffect(() => {
    if (failedFetchIdsKey.length > 0) {
      addToast(
        Notification.create(
          'Fetching Documents Failed',
          `There was an error fetching documents. Please try again. (${failedFetchIds.join(', ')})`,
        ),
        { appearance: 'error' },
      );
    }
  }, [failedFetchIdsKey]);

  const updateDocumentStatus = async (
    document: DocumentRecord & { id: string },
    status: string,
    reason?: string,
  ) => {
    const documentInput = buildDocumentInput({
      patientId: patient.userId,
      typeId: document.typeId,
      carePlanId: document?.carePlanId,
      careActivityId: document?.careActivityId,
      status,
      ...(reason && { reason }),
    });

    await createDocument({
      document: documentInput,
    });

    setTableData((prevData) =>
      prevData.map((item) => (item.id === document.id ? { ...item, status } : item)),
    );
  };

  const handleToggleStatus = async (document: DocumentRecord & { id: string }, status: string) => {
    try {
      setIsLoading(true);
      await updateDocumentStatus(document, status);
    } catch (err) {
      const errorMessage = getErrorMessage(err);
      addToast(Notification.create('Status Update Failed', errorMessage), {
        appearance: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const uploadDocumentData = async (documentData) => {
    try {
      setIsLoading(true);

      let fileType;
      let base64File;
      try {
        const parsed = parseDataUrl(documentData.data);
        fileType = parsed.fileType;
        base64File = parsed.base64File;
      } catch (parseError) {
        addToast(Notification.create('Document Upload Failed', 'Invalid document format'), {
          appearance: 'error',
        });
        return;
      }

      const documentInput = buildDocumentInput({
        patientId: patient.userId,
        typeId: documentData.typeId,
        status: getDocumentStatusByType(documentData.typeId),
        name: documentData.name,
        base64File,
        fileType,
      });

      await createDocument({
        document: documentInput,
      });

      await refetch();
    } catch (err) {
      const errorMessage = getErrorMessage(err);
      addToast(Notification.create('Document Upload Failed', errorMessage), {
        appearance: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const deleteDocumentData = async (id: string, typeId?: string, document?: DocumentRecord) => {
    try {
      setIsLoading(true);
      const carePlanId = document?.carePlanId;
      const careActivityId = document?.careActivityId;

      const hasCarePlanData = carePlanId && careActivityId;
      await deactivateDocument({
        typeId,
        studyId: hasCarePlanData ? undefined : selectedStudyId,
        carePlanId,
        careActivityId,
      });
      setTableData((prevData) => prevData.filter((item) => item.uuid !== id));
    } catch (err) {
      const errorMessage = getErrorMessage(err);
      addToast(Notification.create('Document Deletion Failed', errorMessage), {
        appearance: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const waitForBackendDocumentUpdate = async (
    document: DocumentRecord & { id: string },
    maxAttempts = 8,
    intervalMs = 2000,
    initialDelayMs = 4000,
  ): Promise<boolean> => {
    await new Promise((resolve) => setTimeout(resolve, initialDelayMs));

    const poll = async (attemptsRemaining: number): Promise<boolean> => {
      if (attemptsRemaining <= 0) return false;

      try {
        const result = await fetchDocument(
          document.typeId,
          selectedStudyId,
          document.carePlanId,
          document.careActivityId,
        );
        const latestDocument = result?.data?.getLatestActiveDocument;

        if (latestDocument && latestDocument.status === DocumentStatus.PENDING_QC) {
          return true;
        }
      } catch {
        // Continue polling on fetch errors
      }

      await new Promise((resolve) => setTimeout(resolve, intervalMs));
      return poll(attemptsRemaining - 1);
    };

    return poll(maxAttempts);
  };

  const handleInitialCheckApprove = async (
    document: DocumentRecord & { id: string },
    reason?: string,
  ) => {
    try {
      setIsLoading(true);
      const { data } = await confirmBodyCompQC();
      const statusCode = data?.confirmBodyCompQC?.statusCode;

      if (statusCode >= 200 && statusCode < 300) {
        const isDocumentUpdated = await waitForBackendDocumentUpdate(document);

        if (!isDocumentUpdated) {
          addToast(
            Notification.create(
              'Initial Check Submit Failed',
              'Backend Report Processing timed out. Please try again.',
            ),
            { appearance: 'error' },
          );
          return;
        }

        try {
          await updateDocumentStatus(document, DocumentStatus.PENDING_QC, reason);
        } catch (updateError) {
          addToast(
            Notification.create(
              'Initial Check Submit Failed',
              'Failed to add the reason. The reason provided was not saved. Please try again.',
            ),
            { appearance: 'error' },
          );
          return;
        }

        try {
          await addLogItem(
            selectedStudyId,
            'Body Composition Initial Check Submitted',
            'Document Workflow',
          )(dispatch);
        } catch (logError) {
          addToast(Notification.create('Warning', 'Document updated but failed to log activity'), {
            appearance: 'warning',
          });
          return;
        }

        addToast(MESSAGES.SUCCESS, { appearance: 'success' });
      } else {
        addToast(
          Notification.create(
            'Initial Check Submit Failed',
            `Server returned status code: ${statusCode}`,
          ),
          { appearance: 'error' },
        );
      }
    } catch (err) {
      addToast(
        Notification.create('Initial Check Submit Failed', 'Initial check submission failed'),
        { appearance: 'error' },
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleInitialCheckReject = async (
    document: DocumentRecord & { id: string },
    reason?: string,
  ) => {
    try {
      setIsLoading(true);
      await updateDocumentStatus(document, DocumentStatus.INTERNAL_REJECTED, reason);
      addToast(Notification.create('Success', 'Document rejected successfully'), {
        appearance: 'success',
      });
    } catch (err) {
      const errorMessage = getErrorMessage(err);
      addToast(Notification.create('Rejection Failed', errorMessage), { appearance: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const onInitialCheckSubmit = async (
    document: DocumentRecord & { id: string },
    status: 'approve' | 'reject',
    reason?: string,
  ) => {
    if (status === 'approve') {
      await handleInitialCheckApprove(document, reason);
    } else if (status === 'reject') {
      await handleInitialCheckReject(document, reason);
    }
  };

  return (
    <Section title="Medical Reports">
      {isFetchingDocumentTypes ? (
        'Loading...'
      ) : (
        <DocumentUpload
          isLoading={isLoading || isFetchingDocument}
          patient={patient}
          data={tableData}
          fetchDocument={fetchStudyDocument}
          onUploadDocument={uploadDocumentData}
          onDeleteDocument={deleteDocumentData}
          enableViewDocButton
          enableDeleteDocButton
          isStudyDocument
          onToggleStatus={handleToggleStatus}
          typeOptions={typeOptions}
          userRoles={userRoles}
          onInitialCheckSubmit={onInitialCheckSubmit}
        />
      )}
    </Section>
  );
};

const StudyDocumentsWithApollo = (): ReactElement => {
  const clinicalWorkflowEndpoint = getApiUri('CLINICAL_WORKFLOW');

  const clinicalWorkflowClient = useMemo(
    () =>
      new ApolloClient({
        link: new HttpLink({
          uri: clinicalWorkflowEndpoint,
          credentials: 'include',
        }),
        cache: new InMemoryCache(),
      }),
    [clinicalWorkflowEndpoint],
  );

  return (
    <ApolloProvider client={clinicalWorkflowClient}>
      <StudyDocuments />
    </ApolloProvider>
  );
};

export default StudyDocumentsWithApollo;
